import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/data/content";
import { Reveal, LineReveal } from "./motion/Reveal";
import { scrollToId } from "@/lib/scroll";

export const Services = () => {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);

  return (
    <section id="services" data-testid="services-section" className="section-y bg-ivory">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16 lg:mb-24">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-6">Services</p></Reveal>
            <LineReveal inView as="h2" lines={["Whatever your business", <>needs, <span className="italic text-sage">designed</span> with care.</>]} className="h2" />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 body-lg lg:pb-3">
            Amara Studios creates every kind of website, from focused landing pages to fully custom digital experiences. There is no fixed menu. We shape each project around what your business needs and how your customers behave.
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <ul className="lg:col-span-7 border-t border-taupe-line" role="list">
            {SERVICES.map((s) => {
              const isActive = s.id === active;
              return (
                <li key={s.id} className="border-b border-taupe-line">
                  <button
                    data-testid={`service-card-${s.id}`}
                    onMouseEnter={() => setActive(s.id)}
                    onFocus={() => setActive(s.id)}
                    onClick={() => setActive(s.id)}
                    className="group w-full grid grid-cols-[3rem_1fr_auto] items-center gap-4 py-6 lg:py-7 text-left"
                    aria-expanded={isActive}
                  >
                    <span className={`font-mono text-[11px] tracking-[0.2em] transition-colors ${isActive ? "text-sage" : "text-taupe"}`}>{s.index}</span>
                    <span className={`font-serif text-2xl sm:text-3xl lg:text-4xl font-light transition-[transform,color] duration-500 ease-out ${isActive ? "text-charcoal translate-x-2" : "text-charcoal/60"}`}>{s.title}</span>
                    <ArrowUpRight size={18} className={`transition-[opacity,transform] duration-500 ${isActive ? "opacity-100 text-sage" : "opacity-0 -translate-x-2"}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="lg:hidden overflow-hidden">
                        <div className="pb-6 pl-16 pr-4">
                          <p className="text-sm text-ink/70 font-light leading-relaxed">{s.desc}</p>
                          <div className="flex flex-wrap gap-2 mt-4">{s.items.map((i) => <span key={i} className="text-[11px] tracking-wide border border-taupe-line px-3 py-1.5 rounded-full text-ink/80">{i}</span>)}</div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>

          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-32">
              <AnimatePresence mode="wait">
                <motion.div key={current.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="bg-cream border border-taupe-line p-10 relative overflow-hidden">
                  <span className="absolute -right-6 -top-10 font-serif text-[11rem] leading-none text-charcoal/[0.04] select-none">{current.index}</span>
                  <p className="eyebrow">Service {current.index}</p>
                  <h3 className="font-serif text-4xl font-light text-charcoal mt-6 leading-tight">{current.title}</h3>
                  <p className="body-lg mt-6">{current.desc}</p>
                  <ul className="mt-8 space-y-3">
                    {current.items.map((i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-ink/80"><span className="h-px w-6 bg-sage" />{i}</li>
                    ))}
                  </ul>
                  <button data-testid="services-cta" onClick={() => scrollToId("contact")} className="btn btn-dark mt-10"><span>Enquire Now</span></button>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
